<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { dispatch, getMealPhotoUrl, getNearbyRestaurants, join, logout as logoutApi, restoreSession, uploadMealPhoto } from './api';
import { isValidAmount, parseOptionalAmount } from './money';
import { avatarOptions } from './seed';
import type { AppAction, AppState, Meal, Restaurant, TabId } from './types';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const tabs: Array<{ id: TabId; icon: string; label: string }> = [
  { id: 'nearby', icon: '⌖', label: '发现' },
  { id: 'favorites', icon: '♡', label: '收藏池' },
  { id: 'draw', icon: '✦', label: '抽一个' },
  { id: 'meals', icon: '▤', label: '聚餐' },
  { id: 'me', icon: '◉', label: '我的' },
];

const activeTab = ref<TabId>('nearby');
const state = ref<AppState | null>(null);
const loading = ref(true);
const joining = ref(false);
const mutating = ref(false);
const joinCode = ref('');
const joinName = ref('');
const selectedAvatarIndex = ref(0);
const errorMessage = ref('');
const toast = ref('');
const search = ref('');
const category = ref('全部');
const selectedRestaurant = ref<Restaurant | null>(null);
const showRating = ref(false);
const ratingScore = ref(5);
const ratingComment = ref('');
const showMealForm = ref(false);
const selectedMeal = ref<Meal | null>(null);
const mealRestaurantId = ref('');
const mealParticipantIds = ref<string[]>([]);
const mealDate = ref(new Date().toISOString().slice(0, 16));
const mealDishes = ref('');
const mealNote = ref('');
const mealExpense = ref<string | number>('');
const mealPersonalExpense = ref<string | number>('');
const mealRatingScore = ref(0);
const mealPhotoFiles = ref<File[]>([]);
const mealPhotoPreviews = ref<string[]>([]);
const mealPhotoUrls = ref<Record<string, string>>({});
const photoViewerUrl = ref('');
const uploadingPhotos = ref(false);
const showBillForm = ref(false);
const billMealId = ref('');
const billAmount = ref<string | number>('');
const billPersonalAmount = ref<string | number>('');
const billFeedback = ref('');
const lastOperationError = ref('');
const drawCategory = ref('全部');
const selectedDrawCandidateIds = ref<string[]>([]);
const showDrawPicker = ref(false);
const drawing = ref(false);
const drawResult = ref<Restaurant | null>(null);
const installPrompt = ref<InstallPromptEvent | null>(null);
const showInstallHelp = ref(false);
const isOnline = ref(navigator.onLine);
const locating = ref(false);
const expandingNearby = ref(false);
const locationMessage = ref('定位后会自动加载真实附近餐馆');
const lastLocation = ref<{ latitude: number; longitude: number } | null>(null);
const hasLoadedNearby = ref(false);
const nearbyRestaurantIds = ref<string[]>([]);
const visibleRestaurantLimit = ref(60);
const showRestaurantForm = ref(false);
const customRestaurantName = ref('');
const customRestaurantCategory = ref('餐馆');
const customRestaurantAddress = ref('');
const customAddToFavorites = ref(true);
const returnToMealAfterRestaurant = ref(false);
let nearbyRequestSequence = 0;

const categories = computed(() => [
  '全部',
  ...new Set(state.value?.restaurants.map((item) => item.category) ?? []),
]);

const filteredRestaurants = computed(() => {
  return (state.value?.restaurants ?? []).filter((item) => {
    const nearbyMatch = !hasLoadedNearby.value || nearbyRestaurantIds.value.includes(item.id);
    const categoryMatch = category.value === '全部' || item.category === category.value;
    const keywordMatch = matchesRestaurantKeyword(item, search.value);
    return nearbyMatch && categoryMatch && keywordMatch;
  });
});

const visibleRestaurants = computed(() => filteredRestaurants.value.slice(0, visibleRestaurantLimit.value));

const favoriteRestaurants = computed(() =>
  (state.value?.favorites ?? [])
    .map((favorite) => ({ favorite, restaurant: findRestaurant(favorite.restaurantId) }))
    .filter((item): item is { favorite: AppState['favorites'][number]; restaurant: Restaurant } => Boolean(item.restaurant)),
);

const myMeals = computed(() => {
  if (!state.value) return [];
  const userId = state.value.currentUser.id;
  return [...state.value.meals]
    .filter((meal) => meal.creatorUserId === userId || meal.participantUserIds.includes(userId))
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
});

const allMeals = computed(() => [...(state.value?.meals ?? [])].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)));

const billSummary = computed(() => {
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const groupExpenses = allMeals.value.filter((meal) => Number.isFinite(meal.expenseAmount));
  const personalExpenses = allMeals.value.filter((meal) => Number.isFinite(personalExpenseFor(meal)));
  const sumFrom = (meals: Meal[], start: Date, amount: (meal: Meal) => number) => meals
    .filter((meal) => new Date(meal.occurredAt) >= start && new Date(meal.occurredAt) <= now)
    .reduce((sum, meal) => sum + amount(meal), 0);
  return {
    personalWeek: sumFrom(personalExpenses, weekStart, (meal) => personalExpenseFor(meal) ?? 0),
    personalMonth: sumFrom(personalExpenses, monthStart, (meal) => personalExpenseFor(meal) ?? 0),
    personalTotal: personalExpenses.reduce((sum, meal) => sum + (personalExpenseFor(meal) ?? 0), 0),
    groupMonth: sumFrom(groupExpenses, monthStart, (meal) => meal.expenseAmount ?? 0),
    count: new Set([...groupExpenses, ...personalExpenses].map((meal) => meal.id)).size,
  };
});

const myRatings = computed(() => {
  if (!state.value) return [];
  return state.value.ratings.filter((item) => item.userId === state.value?.currentUser.id);
});

const myDrawCount = computed(() => state.value
  ? state.value.draws.filter((item) => item.userId === state.value?.currentUser.id).length
  : 0);

const categoryDrawCandidates = computed(() => favoriteRestaurants.value
  .map((item) => item.restaurant)
  .filter((item) => drawCategory.value === '全部' || item.category === drawCategory.value));

const drawCandidates = computed(() => {
  const selected = new Set(selectedDrawCandidateIds.value);
  return categoryDrawCandidates.value.filter((item) => selected.has(item.id));
});

function findRestaurant(id: string): Restaurant | undefined {
  return state.value?.restaurants.find((item) => item.id === id);
}

function findUser(id: string) {
  return state.value?.users.find((item) => item.id === id);
}

function favoriteFor(id: string) {
  return state.value?.favorites.find((item) => item.restaurantId === id);
}

function ratingsFor(id: string) {
  return state.value?.ratings.filter((item) => item.restaurantId === id) ?? [];
}

function mealCreatorRating(meal: Meal) {
  return state.value?.ratings.find((item) => item.restaurantId === meal.restaurantId && item.userId === meal.creatorUserId);
}

function personalExpenseFor(meal: Meal, userId = state.value?.currentUser.id) {
  if (!userId) return undefined;
  return meal.personalExpenses?.[userId];
}

function averageRating(id: string): string {
  const items = ratingsFor(id);
  if (!items.length) return '暂无';
  return (items.reduce((sum, item) => sum + item.score, 0) / items.length).toFixed(1);
}

function matchesRestaurantKeyword(restaurant: Restaurant, keyword: string): boolean {
  const normalized = keyword.trim().toLowerCase();
  if (!normalized) return true;
  const aliases: Record<string, string[]> = {
    火锅: ['hot_pot'], 烧烤: ['barbecue', 'bbq'], 咖啡: ['coffee', 'cafe'],
    面: ['noodle'], 披萨: ['pizza'], 汉堡: ['burger'], 中餐: ['chinese'],
    日料: ['japanese', 'sushi'], 西餐: ['western'], 快餐: ['fast_food'],
  };
  const searchable = `${restaurant.name} ${restaurant.category} ${restaurant.address} ${restaurant.tags.join(' ')}`.toLowerCase();
  return [normalized, ...(aliases[normalized] ?? [])].some((term) => searchable.includes(term));
}

function setTab(tab: TabId) {
  activeTab.value = tab;
  window.location.hash = tab;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function notify(message: string) {
  toast.value = message;
  window.setTimeout(() => { if (toast.value === message) toast.value = ''; }, 2400);
}

async function perform(action: AppAction, success?: string): Promise<boolean> {
  if (!state.value || mutating.value) return false;
  lastOperationError.value = '';
  mutating.value = true;
  try {
    const transientNearby = state.value.restaurants.filter((item) => nearbyRestaurantIds.value.includes(item.id));
    const nextState = await dispatch(state.value, action);
    const merged = new Map(nextState.restaurants.map((item) => [item.id, item]));
    for (const restaurant of transientNearby) if (!merged.has(restaurant.id)) merged.set(restaurant.id, restaurant);
    state.value = { ...nextState, restaurants: [...merged.values()] };
    if (success) notify(success);
    return true;
  } catch (error) {
    lastOperationError.value = error instanceof Error ? error.message : '操作失败，请重试';
    notify(lastOperationError.value);
    return false;
  } finally {
    mutating.value = false;
  }
}

async function submitJoin() {
  if (!joinName.value.trim()) {
    errorMessage.value = '先告诉大家怎么称呼你';
    return;
  }
  const option = avatarOptions[selectedAvatarIndex.value];
  if (!option) return;
  joining.value = true;
  errorMessage.value = '';
  try {
    state.value = await join(joinCode.value, joinName.value.trim(), option[0], option[1]);
    notify('欢迎加入饭搭子');
    void locate(true);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '加入失败';
  } finally {
    joining.value = false;
  }
}

async function toggleFavorite(restaurantId: string) {
  const removing = Boolean(favoriteFor(restaurantId));
  await perform({ type: 'FAVORITE_TOGGLE', requestId: crypto.randomUUID(), payload: { restaurantId, restaurant: findRestaurant(restaurantId) } }, removing ? '已移出收藏池' : '已加入全员收藏池');
}

async function toggleSupport(restaurantId: string) {
  await perform({ type: 'SUPPORT_TOGGLE', requestId: crypto.randomUUID(), payload: { restaurantId } }, '收藏态度已更新');
}

function startRating(restaurant: Restaurant) {
  selectedRestaurant.value = restaurant;
  const mine = state.value?.ratings.find((item) => item.restaurantId === restaurant.id && item.userId === state.value?.currentUser.id);
  ratingScore.value = mine?.score ?? 5;
  ratingComment.value = mine?.comment ?? '';
  showRating.value = true;
}

async function submitRating() {
  if (!selectedRestaurant.value) return;
  await perform({
    type: 'RATE_RESTAURANT',
    requestId: crypto.randomUUID(),
    payload: { restaurantId: selectedRestaurant.value.id, restaurant: selectedRestaurant.value, score: ratingScore.value, comment: ratingComment.value.trim() },
  }, '评分已保存');
  showRating.value = false;
}

function startMeal(restaurantId = '') {
  mealRestaurantId.value = restaurantId || state.value?.restaurants[0]?.id || '';
  mealParticipantIds.value = state.value ? [state.value.currentUser.id] : [];
  mealDate.value = new Date().toISOString().slice(0, 16);
  mealDishes.value = '';
  mealNote.value = '';
  mealExpense.value = '';
  mealPersonalExpense.value = '';
  mealRatingScore.value = 0;
  clearMealPhotoSelection();
  showMealForm.value = true;
}

function toggleParticipant(userId: string) {
  mealParticipantIds.value = mealParticipantIds.value.includes(userId)
    ? mealParticipantIds.value.filter((id) => id !== userId)
    : [...mealParticipantIds.value, userId];
}

async function submitMeal() {
  if (!mealRestaurantId.value || !mealParticipantIds.value.length) {
    notify('请选择餐馆和至少一位参与者');
    return;
  }
  const expenseAmount = parseOptionalAmount(mealExpense.value);
  const personalExpenseAmount = parseOptionalAmount(mealPersonalExpense.value);
  if (!isValidAmount(expenseAmount)) {
    notify('请输入正确的聚餐金额');
    return;
  }
  if (!isValidAmount(personalExpenseAmount)) {
    notify('请输入正确的个人金额');
    return;
  }
  uploadingPhotos.value = true;
  let photoKeys: string[] = [];
  try {
    const compressed = await Promise.all(mealPhotoFiles.value.map((file) => compressMealPhoto(file)));
    photoKeys = await Promise.all(compressed.map((blob) => uploadMealPhoto(blob)));
  } catch (error) {
    notify(error instanceof Error ? error.message : '照片上传失败，请重试');
    uploadingPhotos.value = false;
    return;
  }
  uploadingPhotos.value = false;
  const restaurant = findRestaurant(mealRestaurantId.value);
  const created = await perform({
    type: 'CREATE_MEAL',
    requestId: crypto.randomUUID(),
    payload: {
      restaurantId: mealRestaurantId.value,
      restaurant,
      participantUserIds: mealParticipantIds.value,
      occurredAt: new Date(mealDate.value).toISOString(),
      dishes: mealDishes.value.split(/[，,、\n]/).map((item) => item.trim()).filter(Boolean),
      note: mealNote.value.trim(),
      photoKeys,
      expenseAmount,
      personalExpenseAmount,
    },
  }, '这顿饭已经记下了');
  if (!created) return;
  if (mealRatingScore.value > 0 && restaurant) {
    await perform({
      type: 'RATE_RESTAURANT',
      requestId: crypto.randomUUID(),
      payload: { restaurantId: restaurant.id, restaurant, score: mealRatingScore.value, comment: '' },
    }, '餐馆评分已保存');
  }
  void loadMealPhotoUrls();
  clearMealPhotoSelection();
  showMealForm.value = false;
  setTab('meals');
}

function openBillForm(meal?: Meal) {
  const target = meal ?? allMeals.value[0];
  if (!target) {
    notify('先记录一顿聚餐，再补录账单');
    return;
  }
  billMealId.value = target.id;
  billAmount.value = target.expenseAmount === undefined ? '' : String(target.expenseAmount);
  billPersonalAmount.value = personalExpenseFor(target) === undefined ? '' : String(personalExpenseFor(target));
  billFeedback.value = '';
  showBillForm.value = true;
}

async function submitBillExpense() {
  billFeedback.value = '';
  const amount = parseOptionalAmount(billAmount.value);
  const personalAmount = parseOptionalAmount(billPersonalAmount.value);
  if (!billMealId.value || (amount === undefined && personalAmount === undefined)) {
    billFeedback.value = '请至少填写总金额或个人金额';
    notify('请至少填写总金额或个人金额');
    return;
  }
  if (!isValidAmount(amount) || !isValidAmount(personalAmount)) {
    billFeedback.value = '请填写正确的金额';
    notify('请填写正确的金额');
    return;
  }
  billFeedback.value = '正在保存账单…';
  const saved = await perform({
    type: 'UPDATE_MEAL_EXPENSE',
    requestId: crypto.randomUUID(),
    payload: { mealId: billMealId.value, amount, personalAmount },
  }, '账单金额已保存');
  if (saved) {
    if (selectedMeal.value?.id === billMealId.value) selectedMeal.value = state.value?.meals.find((item) => item.id === billMealId.value) ?? null;
    showBillForm.value = false;
  } else {
    billFeedback.value = lastOperationError.value || '保存失败，请检查网络后重试';
  }
}

function selectBillMeal(mealId: string) {
  billMealId.value = mealId;
  const meal = allMeals.value.find((item) => item.id === mealId);
  billAmount.value = meal?.expenseAmount === undefined ? '' : String(meal.expenseAmount);
  billPersonalAmount.value = meal && personalExpenseFor(meal) !== undefined ? String(personalExpenseFor(meal)) : '';
}

function handleBillMealChange(event: Event) {
  selectBillMeal((event.target as HTMLSelectElement).value);
}

function clearMealPhotoSelection() {
  for (const url of mealPhotoPreviews.value) URL.revokeObjectURL(url);
  mealPhotoFiles.value = [];
  mealPhotoPreviews.value = [];
}

function selectMealPhotos(event: Event) {
  const input = event.target as HTMLInputElement;
  const available = Math.max(0, 4 - mealPhotoFiles.value.length);
  const selected = [...(input.files ?? [])].filter((file) => file.type.startsWith('image/')).slice(0, available);
  if (!selected.length) {
    if (available === 0) notify('每次聚餐最多上传 4 张照片');
    input.value = '';
    return;
  }
  mealPhotoFiles.value = [...mealPhotoFiles.value, ...selected];
  mealPhotoPreviews.value = [...mealPhotoPreviews.value, ...selected.map((file) => URL.createObjectURL(file))];
  input.value = '';
}

function removeMealPhoto(index: number) {
  const preview = mealPhotoPreviews.value[index];
  if (preview) URL.revokeObjectURL(preview);
  mealPhotoFiles.value = mealPhotoFiles.value.filter((_, itemIndex) => itemIndex !== index);
  mealPhotoPreviews.value = mealPhotoPreviews.value.filter((_, itemIndex) => itemIndex !== index);
}

async function compressMealPhoto(file: File): Promise<Blob> {
  const sourceUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = sourceUrl;
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('照片无法读取'));
    });
    const scale = Math.min(1, 1280 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('当前设备无法处理照片');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.76));
    if (!blob || blob.size > 1_500_000) throw new Error('照片处理后仍过大，请换一张照片');
    return blob;
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

async function loadMealPhotoUrls() {
  const keys = [...new Set((state.value?.meals ?? []).flatMap((meal) => meal.photoKeys ?? []))];
  for (const key of keys) {
    if (mealPhotoUrls.value[key]) continue;
    try {
      const url = await getMealPhotoUrl(key);
      mealPhotoUrls.value = { ...mealPhotoUrls.value, [key]: url };
    } catch {
      // Keep the record usable when one photo is temporarily unavailable.
    }
  }
}

function openPhotoViewer(photoKey: string) {
  const url = mealPhotoUrls.value[photoKey];
  if (!url) {
    notify('照片正在加载，请稍后再试');
    return;
  }
  photoViewerUrl.value = url;
}

function openCustomRestaurant(forMeal = false, prefillName = '') {
  customRestaurantName.value = prefillName;
  customRestaurantCategory.value = /铁板烧|烧烤|烤肉/.test(prefillName) ? '烧烤' : '餐馆';
  customRestaurantAddress.value = '';
  customAddToFavorites.value = !forMeal;
  returnToMealAfterRestaurant.value = forMeal;
  if (forMeal) showMealForm.value = false;
  showRestaurantForm.value = true;
}

function closeCustomRestaurant() {
  showRestaurantForm.value = false;
  if (returnToMealAfterRestaurant.value) showMealForm.value = true;
  returnToMealAfterRestaurant.value = false;
}

async function submitCustomRestaurant() {
  const name = customRestaurantName.value.trim();
  if (!name) {
    notify('请填写餐馆名称');
    return;
  }
  const restaurant: Restaurant = {
    id: `manual-${crypto.randomUUID()}`,
    name,
    category: customRestaurantCategory.value.trim() || '其他',
    address: customRestaurantAddress.value.trim() || '成员未填写地址',
    tags: ['成员自建'],
    emoji: '🍽️',
  };
  const created = await perform({ type: 'CREATE_RESTAURANT', requestId: crypto.randomUUID(), payload: { restaurant } }, '自建餐馆已保存');
  if (!created) return;
  if (customAddToFavorites.value) {
    await perform({ type: 'FAVORITE_TOGGLE', requestId: crypto.randomUUID(), payload: { restaurantId: restaurant.id } }, '已加入全员收藏池');
  }
  showRestaurantForm.value = false;
  if (returnToMealAfterRestaurant.value) {
    mealRestaurantId.value = restaurant.id;
    showMealForm.value = true;
  } else {
    selectedRestaurant.value = findRestaurant(restaurant.id) ?? restaurant;
  }
  returnToMealAfterRestaurant.value = false;
}

async function drawOne() {
  if (!drawCandidates.value.length || drawing.value) {
    if (!favoriteRestaurants.value.length) notify('先往收藏池放几家餐馆吧');
    else if (!categoryDrawCandidates.value.length) notify('当前分类没有收藏餐馆');
    else if (!drawCandidates.value.length) notify('请先勾选至少一家餐馆');
    return;
  }
  showDrawPicker.value = false;
  drawing.value = true;
  drawResult.value = null;
  const candidates = drawCandidates.value;
  let ticks = 0;
  const timer = window.setInterval(() => {
    drawResult.value = candidates[Math.floor(Math.random() * candidates.length)] ?? null;
    ticks += 1;
    if (ticks >= 10) window.clearInterval(timer);
  }, 75);
  window.setTimeout(async () => {
    const bytes = new Uint32Array(1);
    crypto.getRandomValues(bytes);
    const selected = candidates[(bytes[0] ?? 0) % candidates.length];
    if (!selected) return;
    drawResult.value = selected;
    drawing.value = false;
    await perform({
      type: 'CREATE_DRAW',
      requestId: crypto.randomUUID(),
      payload: { category: drawCategory.value === '全部' ? undefined : drawCategory.value, candidateIds: candidates.map((item) => item.id), restaurantId: selected.id },
    });
  }, 900);
}

function selectAllDrawCandidates() {
  selectedDrawCandidateIds.value = categoryDrawCandidates.value.map((item) => item.id);
  drawResult.value = null;
}

function clearDrawCandidates() {
  selectedDrawCandidateIds.value = [];
  drawResult.value = null;
}

function handleDrawCandidateChange() {
  drawResult.value = null;
}

function applyNearbyResults(nearby: Restaurant[]) {
  if (!state.value) return;
  const previousNearby = new Set(nearbyRestaurantIds.value);
  const persistentIds = new Set([
    ...state.value.favorites.map((item) => item.restaurantId),
    ...state.value.ratings.map((item) => item.restaurantId),
    ...state.value.meals.map((item) => item.restaurantId),
  ]);
  nearbyRestaurantIds.value = nearby.map((item) => item.id);
  const retained = state.value.restaurants.filter((item) => !previousNearby.has(item.id) || persistentIds.has(item.id));
  const merged = new Map(retained.map((item) => [item.id, item]));
  for (const restaurant of nearby) merged.set(restaurant.id, restaurant);
  state.value = {
    ...state.value,
    restaurants: [...merged.values()].sort((a, b) => (a.distanceMeters ?? Infinity) - (b.distanceMeters ?? Infinity)),
  };
  visibleRestaurantLimit.value = 60;
  category.value = '全部';
}

async function loadNearby(latitude: number, longitude: number, keyword = '') {
  if (!state.value) return;
  const sequence = ++nearbyRequestSequence;
  locating.value = true;
  expandingNearby.value = false;
  locationMessage.value = keyword ? `正在 10 公里内搜索“${keyword}”…` : '正在搜索 10 公里内的餐馆…';
  try {
    const nearby = await getNearbyRestaurants(latitude, longitude, 10_000, keyword, 'fast');
    if (sequence !== nearbyRequestSequence) return;
    hasLoadedNearby.value = true;
    applyNearbyResults(nearby);
    locating.value = false;
    expandingNearby.value = true;
    locationMessage.value = nearby.length ? `已先显示 ${nearby.length} 家，正在补全 10 公里结果…` : '快速结果为空，正在继续完整搜索…';
    const complete = await getNearbyRestaurants(latitude, longitude, 10_000, keyword, 'full');
    if (sequence !== nearbyRequestSequence) return;
    applyNearbyResults(complete);
    locationMessage.value = complete.length ? `10 公里内已加载 ${complete.length} 家餐馆` : '10 公里内暂未找到开放地图餐馆数据';
    notify(locationMessage.value);
  } catch (error) {
    locationMessage.value = hasLoadedNearby.value && nearbyRestaurantIds.value.length
      ? `已保留当前 ${nearbyRestaurantIds.value.length} 家，完整搜索暂时不可用`
      : error instanceof Error ? error.message : '附近餐馆加载失败，请稍后重试';
    notify(locationMessage.value);
  } finally {
    if (sequence === nearbyRequestSequence) {
      locating.value = false;
      expandingNearby.value = false;
    }
  }
}

function locate(silent = false) {
  if (locating.value) return;
  if (!navigator.geolocation) {
    notify('当前浏览器不支持定位');
    return;
  }
  locating.value = true;
  locationMessage.value = '正在获取位置…';
  navigator.geolocation.getCurrentPosition(({ coords }) => {
    lastLocation.value = { latitude: coords.latitude, longitude: coords.longitude };
    locating.value = false;
    void loadNearby(coords.latitude, coords.longitude, search.value);
  }, () => {
    locating.value = false;
    locationMessage.value = '未获得定位权限，可授权后重试';
    if (!silent) notify(locationMessage.value);
  }, { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 });
}

function refreshNearbySearch() {
  const position = lastLocation.value;
  if (!position) {
    locate();
    return;
  }
  void loadNearby(position.latitude, position.longitude, search.value);
}

function distance(lat1: number, lon1: number, lat2: number, lon2: number) {
  const rad = Math.PI / 180;
  const a = Math.sin((lat2 - lat1) * rad / 2) ** 2
    + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin((lon2 - lon1) * rad / 2) ** 2;
  return Math.round(12_742_000 * Math.asin(Math.sqrt(a)));
}

function formatDistance(value?: number) {
  if (value === undefined) return '';
  return value < 1000 ? `${value}m` : `${(value / 1000).toFixed(1)}km`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

function formatMoney(value: number) {
  return new Intl.NumberFormat('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value);
}

async function installApp() {
  if (installPrompt.value) {
    await installPrompt.value.prompt();
    const choice = await installPrompt.value.userChoice;
    if (choice.outcome === 'accepted') notify('正在安装到桌面');
    installPrompt.value = null;
  } else {
    showInstallHelp.value = true;
  }
}

function logOut() {
  if (!window.confirm('确认退出当前饭搭子空间？')) return;
  logoutApi();
  state.value = null;
}

function online() { isOnline.value = true; }
function offline() { isOnline.value = false; }
function captureInstall(event: Event) {
  event.preventDefault();
  installPrompt.value = event as InstallPromptEvent;
}

watch(
  () => (state.value?.meals ?? []).flatMap((meal) => meal.photoKeys ?? []).join('|'),
  () => { void loadMealPhotoUrls(); },
);

onMounted(async () => {
  const hash = window.location.hash.slice(1) as TabId;
  if (tabs.some((tab) => tab.id === hash)) activeTab.value = hash;
  window.addEventListener('online', online);
  window.addEventListener('offline', offline);
  window.addEventListener('beforeinstallprompt', captureInstall);
  state.value = await restoreSession();
  loading.value = false;
  if (state.value) void locate(true);
});

onBeforeUnmount(() => {
  window.removeEventListener('online', online);
  window.removeEventListener('offline', offline);
  window.removeEventListener('beforeinstallprompt', captureInstall);
  clearMealPhotoSelection();
  for (const url of Object.values(mealPhotoUrls.value)) if (url.startsWith('blob:')) URL.revokeObjectURL(url);
});
</script>

<template>
  <div v-if="loading" class="splash">
    <div class="logo-mark">625</div>
    <p>正在摆好碗筷…</p>
  </div>

  <main v-else-if="!state" class="join-page">
    <section class="join-hero">
      <div class="mini-brand"><span class="brand-dot">625</span><span>吃什么</span></div>
      <div class="hero-visual" aria-hidden="true">
        <span class="hero-bowl">🍚</span><span class="orbit orbit-a">🍜</span><span class="orbit orbit-b">🍲</span><span class="orbit orbit-c">🧋</span>
      </div>
      <p class="eyebrow">只属于熟人的饭桌</p>
      <h1>别纠结了，<br /><em>一起吃点好的。</em></h1>
      <p class="join-copy">共享收藏、认真评分、随机抽签，再把每一顿值得记住的饭保存下来。</p>
    </section>
    <section class="join-card">
      <label>大家怎么称呼你</label>
      <input v-model="joinName" maxlength="12" autocomplete="nickname" placeholder="例如：小林" />
      <label>选择你的饭搭子头像</label>
      <div class="avatar-grid compact">
        <button v-for="(option, index) in avatarOptions" :key="index" :class="{ selected: selectedAvatarIndex === index }" :style="{ background: option[1] }" :aria-label="`头像 ${index + 1}`" @click="selectedAvatarIndex = index">{{ option[0] }}</button>
      </div>
      <label>熟人邀请码</label>
      <input v-model="joinCode" inputmode="numeric" autocomplete="one-time-code" placeholder="输入朋友发来的邀请码" @keyup.enter="submitJoin" />
      <p class="field-note">首次使用会创建账号；换设备时输入同一邀请码和原昵称，会自动恢复原账号及历史数据。</p>
      <p v-if="errorMessage" class="form-error">{{ errorMessage }}</p>
      <button class="primary wide" :disabled="joining" @click="submitJoin">{{ joining ? '正在加入…' : '进入饭桌' }}</button>
      <p class="privacy-note">不需要手机号或邮箱，仅保存你主动填写的昵称和用餐记录。</p>
    </section>
  </main>

  <div v-else class="app-shell">
    <header class="topbar">
      <div class="brand"><span class="brand-dot">625</span><strong>吃什么</strong></div>
      <div class="top-actions"><span class="status-pill" :class="{ offline: !isOnline }"><i></i>{{ isOnline ? '已同步' : '离线' }}</span><button class="avatar-button" :style="{ background: state.currentUser.avatarColor }" @click="setTab('me')">{{ state.currentUser.avatar }}</button></div>
    </header>

    <div class="content">
      <section v-if="activeTab === 'nearby'" class="page">
        <div class="page-intro"><div><p class="eyebrow">今天也要好好吃饭</p><h1>想吃点什么？</h1></div><div class="page-actions"><button class="secondary small" @click="openCustomRestaurant()">＋ 自建</button><button class="soft-button" :disabled="locating" @click="locate()">{{ locating ? '定位中…' : expandingNearby ? '补全中…' : '⌖ 定位刷新' }}</button></div></div>
        <div class="search-box"><span>⌕</span><input v-model="search" placeholder="搜附近餐馆、地址或标签" @keyup.enter="refreshNearbySearch" /><button class="search-submit" :disabled="locating" @click="refreshNearbySearch">搜索</button></div>
        <p class="location-note">{{ locationMessage }} · 数据来源：OpenStreetMap 开放地图</p>
        <div class="chip-row"><button v-for="item in categories" :key="item" :class="{ active: category === item }" @click="category = item">{{ item }}</button></div>
        <div class="section-heading"><h2>10 公里附近好味道</h2><span>{{ filteredRestaurants.length }} 家</span></div>
        <div class="restaurant-list">
          <article v-for="restaurant in visibleRestaurants" :key="restaurant.id" class="restaurant-card" @click="selectedRestaurant = restaurant">
            <div class="food-icon">{{ restaurant.emoji }}</div>
            <div class="restaurant-main">
              <div class="title-line"><h3>{{ restaurant.name }}</h3><span v-if="restaurant.distanceMeters">{{ formatDistance(restaurant.distanceMeters) }}</span></div>
              <p>{{ restaurant.category }} · 人均 ¥{{ restaurant.averagePrice ?? '—' }}</p>
              <div class="rating-line"><strong>内部 {{ averageRating(restaurant.id) }}</strong><span>{{ ratingsFor(restaurant.id).length }} 人评价</span><i></i><span>页面参考 {{ restaurant.referenceRating ?? '—' }}</span></div>
              <div class="tag-row"><span v-for="tag in restaurant.tags" :key="tag">{{ tag }}</span></div>
            </div>
            <button class="heart" :class="{ active: favoriteFor(restaurant.id) }" aria-label="收藏" @click.stop="toggleFavorite(restaurant.id)">{{ favoriteFor(restaurant.id) ? '♥' : '♡' }}</button>
          </article>
        </div>
        <button v-if="visibleRestaurants.length < filteredRestaurants.length" class="secondary wide load-more" @click="visibleRestaurantLimit += 60">再显示 {{ Math.min(60, filteredRestaurants.length - visibleRestaurants.length) }} 家</button>
        <div v-if="!visibleRestaurants.length" class="empty-state"><span>🥢</span><h3>开放地图暂时没找到</h3><p>美团等商业平台的商户不一定收录在开放地图中，可以直接自建。</p><button v-if="search.trim()" class="primary" @click="openCustomRestaurant(false, search.trim())">用“{{ search.trim() }}”新建餐馆</button><button v-else class="secondary" @click="openCustomRestaurant()">自建餐馆</button></div>
      </section>

      <section v-else-if="activeTab === 'favorites'" class="page">
        <div class="page-intro"><div><p class="eyebrow">全员共享</p><h1>我们的收藏池</h1></div><div class="page-actions"><button class="secondary small" @click="openCustomRestaurant()">＋ 新建餐馆</button><div class="member-stack"><span v-for="user in state.users.slice(0, 3)" :key="user.id" :style="{ background: user.avatarColor }">{{ user.avatar }}</span></div></div></div>
        <div class="pool-summary"><div><strong>{{ favoriteRestaurants.length }}</strong><span>候选餐馆</span></div><div><strong>{{ new Set(state.favorites.flatMap(item => item.supporterUserIds)).size }}</strong><span>参与表态</span></div><button @click="setTab('draw')">现在抽一个 →</button></div>
        <div class="restaurant-list">
          <article v-for="item in favoriteRestaurants" :key="item.restaurant.id" class="favorite-card">
            <button class="food-icon" @click="selectedRestaurant = item.restaurant">{{ item.restaurant.emoji }}</button>
            <div class="restaurant-main" @click="selectedRestaurant = item.restaurant"><h3>{{ item.restaurant.name }}</h3><p>由 {{ findUser(item.favorite.createdByUserId)?.displayName ?? '成员' }} 收藏</p><div class="rating-line"><strong>内部 {{ averageRating(item.restaurant.id) }}</strong><span>{{ item.restaurant.category }}</span></div></div>
            <button class="support-button" :class="{ active: item.favorite.supporterUserIds.includes(state.currentUser.id) }" @click="toggleSupport(item.restaurant.id)"><span>👍</span>{{ item.favorite.supporterUserIds.length }}</button>
          </article>
        </div>
        <div v-if="!favoriteRestaurants.length" class="empty-state"><span>♡</span><h3>收藏池还是空的</h3><p>可从地图选择，也可以直接自建餐馆。</p><div class="empty-actions"><button class="secondary" @click="setTab('nearby')">去逛逛</button><button class="primary" @click="openCustomRestaurant()">新建餐馆</button></div></div>
      </section>

      <section v-else-if="activeTab === 'draw'" class="page draw-page">
        <div class="draw-copy"><p class="eyebrow">交给一点好运</p><h1>今天，吃什么？</h1><p>候选只来自大家的共享收藏池。</p></div>
        <div class="draw-machine" :class="{ spinning: drawing }">
          <div class="draw-glow"></div>
          <div v-if="drawResult" class="draw-result"><span>{{ drawResult.emoji }}</span><p>{{ drawing ? '正在认真挑选…' : '就决定是它了' }}</p><h2>{{ drawResult.name }}</h2><small>{{ drawResult.category }} · 人均 ¥{{ drawResult.averagePrice }}</small></div>
          <div v-else class="draw-placeholder"><span>✦</span><h2>{{ drawCandidates.length }} 家已选</h2><p>先从收藏池勾选，再交给运气</p></div>
        </div>
        <label class="select-label">想吃的分类</label>
        <select v-model="drawCategory" class="select-control"><option v-for="item in categories" :key="item">{{ item }}</option></select>
        <button type="button" class="draw-picker-toggle" :class="{ open: showDrawPicker }" :disabled="drawing" @click="showDrawPicker = !showDrawPicker">
          <span class="draw-picker-toggle-icon">☑</span><span><strong>选择候选餐馆</strong><small v-if="drawCandidates.length">已选 {{ drawCandidates.length }} 家 · {{ drawCandidates.slice(0, 2).map(item => item.name).join('、') }}{{ drawCandidates.length > 2 ? '…' : '' }}</small><small v-else>打开后从收藏池勾选</small></span><b>{{ showDrawPicker ? '⌃' : '⌄' }}</b>
        </button>
        <section v-if="showDrawPicker" class="draw-picker">
          <header><div><strong>勾选本次候选</strong><span>已选 {{ drawCandidates.length }} / {{ categoryDrawCandidates.length }} 家</span></div><div><button type="button" @click="selectAllDrawCandidates">全选</button><button type="button" @click="clearDrawCandidates">清空</button></div></header>
          <div v-if="categoryDrawCandidates.length" class="draw-candidate-list">
            <label v-for="restaurant in categoryDrawCandidates" :key="restaurant.id" :class="{ selected: selectedDrawCandidateIds.includes(restaurant.id) }">
              <input v-model="selectedDrawCandidateIds" type="checkbox" :value="restaurant.id" :disabled="drawing" @change="handleDrawCandidateChange" />
              <span class="draw-candidate-icon">{{ restaurant.emoji }}</span>
              <span><strong>{{ restaurant.name }}</strong><small>{{ restaurant.category }} · {{ restaurant.address }}</small></span>
              <b>{{ selectedDrawCandidateIds.includes(restaurant.id) ? '✓' : '+' }}</b>
            </label>
          </div>
          <p v-else class="draw-picker-empty">当前分类没有收藏餐馆</p>
          <button type="button" class="draw-picker-done" @click="showDrawPicker = false">完成选择 · 已选 {{ drawCandidates.length }} 家</button>
        </section>
        <button class="draw-button" :disabled="drawing || !drawCandidates.length" @click="drawOne"><span>✦</span>{{ drawing ? '抽取中…' : '从已选餐馆中抽一家' }}</button>
        <button v-if="drawResult && !drawing" class="secondary wide" @click="startMeal(drawResult.id)">就吃这家，记一顿</button>
        <p class="fair-note">使用设备安全随机数，抽签结果会保存在你的历史中。</p>
      </section>

      <section v-else-if="activeTab === 'meals'" class="page">
        <div class="page-intro"><div><p class="eyebrow">一起吃过的日子</p><h1>聚餐记录</h1></div><button class="primary small" @click="startMeal()">＋ 记一顿</button></div>
        <div v-if="allMeals.length" class="timeline">
          <article v-for="meal in allMeals" :key="meal.id" class="meal-card clickable" role="button" tabindex="0" @click="selectedMeal = meal" @keyup.enter="selectedMeal = meal"><div class="meal-date"><strong>{{ new Date(meal.occurredAt).getDate() }}</strong><span>{{ new Intl.DateTimeFormat('zh-CN', { month: 'short', timeZone: 'Asia/Shanghai' }).format(new Date(meal.occurredAt)) }}</span></div><div class="meal-body"><div class="title-line"><h3>{{ findRestaurant(meal.restaurantId)?.name }}</h3><span>{{ formatDate(meal.occurredAt) }}</span></div><small class="meal-creator">由 {{ findUser(meal.creatorUserId)?.displayName ?? '成员' }} 记录</small><p v-if="meal.dishes.length">点了：{{ meal.dishes.join('、') }}</p><p v-if="meal.note">{{ meal.note }}</p><div v-if="meal.expenseAmount !== undefined || personalExpenseFor(meal) !== undefined" class="meal-expense-line"><strong v-if="meal.expenseAmount !== undefined">总计 ¥{{ formatMoney(meal.expenseAmount) }}</strong><strong v-if="personalExpenseFor(meal) !== undefined">我的 ¥{{ formatMoney(personalExpenseFor(meal) ?? 0) }}</strong></div><div v-if="meal.photoKeys?.length" class="meal-photos"><img v-for="photoKey in meal.photoKeys" :key="photoKey" :src="mealPhotoUrls[photoKey]" alt="聚餐照片，点击放大" loading="lazy" role="button" tabindex="0" title="点击放大" @click.stop="openPhotoViewer(photoKey)" @dblclick.stop="openPhotoViewer(photoKey)" @keyup.enter.stop="openPhotoViewer(photoKey)" /></div><div class="meal-card-footer"><div class="meal-people"><span v-for="userId in meal.participantUserIds" :key="userId" :style="{ background: findUser(userId)?.avatarColor }" :title="findUser(userId)?.displayName">{{ findUser(userId)?.avatar }}</span><small>{{ meal.participantUserIds.length }} 人</small></div><b>查看详情 ›</b></div></div></article>
        </div>
        <div v-else class="empty-state tall"><span>🍽️</span><h3>第一顿等你记录</h3><p>选择餐馆和一起吃饭的人，大家的档案会同步出现。</p><button class="primary" @click="startMeal()">记录第一次聚餐</button></div>
      </section>

      <section v-else class="page profile-page">
        <div class="profile-card"><button class="profile-avatar" :style="{ background: state.currentUser.avatarColor }">{{ state.currentUser.avatar }}</button><div><p class="eyebrow">{{ state.currentUser.role === 'OWNER' ? '饭桌创建者' : '饭搭子成员' }}</p><h1>{{ state.currentUser.displayName }}</h1><span>和 {{ state.users.length - 1 }} 位朋友一起吃饭</span></div></div>
        <div class="stats"><div><strong>{{ myMeals.length }}</strong><span>参加聚餐</span></div><div><strong>{{ myRatings.length }}</strong><span>我的评分</span></div><div><strong>{{ myDrawCount }}</strong><span>抽签次数</span></div></div>
        <section class="bill-card"><div class="bill-heading"><div><p class="eyebrow">饭桌共享账单</p><h2>聚餐花费</h2></div><button class="primary small" @click="openBillForm()">＋ 补录账单</button></div><div class="bill-stats"><div><span>我本周</span><strong>¥{{ formatMoney(billSummary.personalWeek) }}</strong></div><div><span>我本月</span><strong>¥{{ formatMoney(billSummary.personalMonth) }}</strong></div><div><span>饭桌本月</span><strong>¥{{ formatMoney(billSummary.groupMonth) }}</strong></div></div><div v-if="billSummary.count" class="bill-list"><button v-for="meal in allMeals.filter(item => item.expenseAmount !== undefined || personalExpenseFor(item) !== undefined).slice(0, 4)" :key="meal.id" @click="openBillForm(meal)"><span><strong>{{ findRestaurant(meal.restaurantId)?.name ?? '聚餐' }}</strong><small>{{ formatDate(meal.occurredAt) }} · 总计 {{ meal.expenseAmount === undefined ? '未填' : `¥${formatMoney(meal.expenseAmount)}` }}</small></span><b>{{ personalExpenseFor(meal) === undefined ? '填个人金额' : `我的 ¥${formatMoney(personalExpenseFor(meal) ?? 0)}` }}</b></button></div><p v-else class="muted">还没有账单，可分别填写整桌总金额和你自己的个人金额。</p></section>
        <div class="profile-section"><h2>我的最近评分</h2><article v-for="rating in myRatings.slice(-3).reverse()" :key="rating.id" class="rating-item"><span>{{ findRestaurant(rating.restaurantId)?.emoji }}</span><div><strong>{{ findRestaurant(rating.restaurantId)?.name }}</strong><p>{{ rating.comment || '没有留下文字' }}</p></div><b>{{ rating.score.toFixed(1) }}</b></article><p v-if="!myRatings.length" class="muted">还没有评分，吃完一顿再回来写。</p></div>
        <div class="settings-list"><button @click="installApp"><span>▣</span><div><strong>安装到手机</strong><small>安卓直接安装；iPhone 添加到主屏幕</small></div><b>›</b></button><button @click="showInstallHelp = true"><span>?</span><div><strong>安装与隐私说明</strong><small>查看数据、定位与离线规则</small></div><b>›</b></button><button class="danger-text" @click="logOut"><span>↪</span><div><strong>退出当前空间</strong><small>本机登录状态将被清除</small></div><b>›</b></button></div>
      </section>
    </div>

    <nav class="bottom-nav"><button v-for="tab in tabs" :key="tab.id" :class="{ active: activeTab === tab.id, draw: tab.id === 'draw' }" @click="setTab(tab.id)"><i>{{ tab.icon }}</i><span>{{ tab.label }}</span></button></nav>

    <div v-if="selectedRestaurant" class="modal-layer" @click.self="selectedRestaurant = null"><section class="sheet restaurant-sheet"><button class="close" @click="selectedRestaurant = null">×</button><div class="detail-hero"><span>{{ selectedRestaurant.emoji }}</span><div><p class="eyebrow">{{ selectedRestaurant.category }}</p><h2>{{ selectedRestaurant.name }}</h2><p>{{ selectedRestaurant.address }}</p></div></div><div class="detail-scores"><div><strong>{{ averageRating(selectedRestaurant.id) }}</strong><span>内部评分 · {{ ratingsFor(selectedRestaurant.id).length }}人</span></div><div><strong>{{ selectedRestaurant.referenceRating ?? '—' }}</strong><span>页面参考 · {{ selectedRestaurant.referenceUpdatedAt?.slice(5, 10) }}</span></div></div><div class="tag-row large"><span v-for="tag in selectedRestaurant.tags" :key="tag">{{ tag }}</span></div><div class="sheet-actions"><button class="secondary" @click="startRating(selectedRestaurant)">☆ 写评分</button><button class="primary" @click="toggleFavorite(selectedRestaurant.id)">{{ favoriteFor(selectedRestaurant.id) ? '移出收藏池' : '加入收藏池' }}</button></div><div class="reviews"><h3>熟人怎么说</h3><article v-for="rating in ratingsFor(selectedRestaurant.id)" :key="rating.id"><span :style="{ background: findUser(rating.userId)?.avatarColor }">{{ findUser(rating.userId)?.avatar }}</span><div><strong>{{ findUser(rating.userId)?.displayName }} · {{ rating.score.toFixed(1) }}</strong><p>{{ rating.comment }}</p></div></article><p v-if="!ratingsFor(selectedRestaurant.id).length" class="muted">还没人评价，等你来当第一个。</p></div></section></div>

    <div v-if="selectedMeal" class="modal-layer" @click.self="selectedMeal = null"><section class="sheet meal-detail-sheet"><button class="close" @click="selectedMeal = null">×</button><p class="eyebrow">聚餐详情</p><h2>{{ findRestaurant(selectedMeal.restaurantId)?.name ?? '餐馆' }}</h2><p class="meal-detail-time">{{ formatDate(selectedMeal.occurredAt) }} · 由 {{ findUser(selectedMeal.creatorUserId)?.displayName ?? '成员' }} 记录</p><div v-if="mealCreatorRating(selectedMeal)" class="meal-rating"><span>本次餐馆评分</span><strong>{{ mealCreatorRating(selectedMeal)?.score.toFixed(1) }} ★</strong></div><div class="meal-rating bill-row"><span>整桌总花费</span><strong>{{ selectedMeal.expenseAmount === undefined ? '未记录' : `¥${formatMoney(selectedMeal.expenseAmount)}` }}</strong></div><div class="meal-rating bill-row personal-row"><span>我的个人金额</span><strong>{{ personalExpenseFor(selectedMeal) === undefined ? '未记录' : `¥${formatMoney(personalExpenseFor(selectedMeal) ?? 0)}` }}</strong><button @click="openBillForm(selectedMeal)">补录/修改</button></div><div v-if="selectedMeal.photoKeys?.length" class="meal-detail-photos"><img v-for="photoKey in selectedMeal.photoKeys" :key="photoKey" :src="mealPhotoUrls[photoKey]" alt="聚餐照片，点击放大" role="button" tabindex="0" title="点击或双击放大" @click.stop="openPhotoViewer(photoKey)" @dblclick.stop="openPhotoViewer(photoKey)" @keyup.enter.stop="openPhotoViewer(photoKey)" /></div><section v-if="selectedMeal.dishes.length || selectedMeal.note" class="meal-detail-copy"><div v-if="selectedMeal.dishes.length"><span>吃了什么</span><p>{{ selectedMeal.dishes.join('、') }}</p></div><div v-if="selectedMeal.note"><span>这一顿的备注</span><p>{{ selectedMeal.note }}</p></div></section><h3 class="attendee-title">一起吃饭的人 · {{ selectedMeal.participantUserIds.length }} 人</h3><div class="attendee-list"><div v-for="userId in selectedMeal.participantUserIds" :key="userId"><span :style="{ background: findUser(userId)?.avatarColor }">{{ findUser(userId)?.avatar }}</span><div><strong>{{ findUser(userId)?.displayName ?? '成员' }}</strong><small>{{ userId === selectedMeal.creatorUserId ? '记录人' : '参与成员' }}<template v-if="personalExpenseFor(selectedMeal, userId) !== undefined"> · ¥{{ formatMoney(personalExpenseFor(selectedMeal, userId) ?? 0) }}</template></small></div></div></div><button class="primary wide" @click="selectedMeal = null">看完了</button></section></div>

    <div v-if="photoViewerUrl" class="photo-lightbox" role="dialog" aria-modal="true" aria-label="聚餐照片大图" @click.self="photoViewerUrl = ''"><button class="photo-lightbox-close" aria-label="关闭大图" @click="photoViewerUrl = ''">×</button><img :src="photoViewerUrl" alt="聚餐照片大图" title="双击关闭" @dblclick="photoViewerUrl = ''" /><p>双击图片或点击关闭按钮返回</p></div>

    <div v-if="showRating" class="modal-layer" @click.self="showRating = false"><section class="sheet form-sheet"><button class="close" @click="showRating = false">×</button><p class="eyebrow">只在熟人之间可见</p><h2>评价 {{ selectedRestaurant?.name }}</h2><div class="score-picker"><button v-for="score in [1, 2, 3, 4, 5]" :key="score" :class="{ active: score <= ratingScore }" @click="ratingScore = score">★</button><strong>{{ ratingScore.toFixed(1) }}</strong></div><label>说两句（可选）</label><textarea v-model="ratingComment" maxlength="200" placeholder="味道、环境、服务，记下真实感受…"></textarea><button class="primary wide" :disabled="mutating" @click="submitRating">保存评分</button></section></div>

    <div v-if="showMealForm" class="modal-layer" @click.self="showMealForm = false"><section class="sheet form-sheet meal-form"><button class="close" @click="showMealForm = false">×</button><p class="eyebrow">留住这一顿</p><h2>记录聚餐</h2><label>去了哪家</label><select v-model="mealRestaurantId" class="select-control"><option value="" disabled>请选择餐馆</option><option v-for="restaurant in state.restaurants" :key="restaurant.id" :value="restaurant.id">{{ restaurant.name }}</option></select><button class="inline-create" @click="openCustomRestaurant(true)">地图上没有？自建一家餐馆</button><label>什么时候</label><input v-model="mealDate" type="datetime-local" /><label>和谁一起 · 已选 {{ mealParticipantIds.length }} 人</label><div class="participant-grid"><button v-for="user in state.users" :key="user.id" :class="{ selected: mealParticipantIds.includes(user.id) }" @click="toggleParticipant(user.id)"><span :style="{ background: user.avatarColor }">{{ user.avatar }}</span><small>{{ user.displayName }}</small></button></div><label>吃了什么（用逗号分隔）</label><input v-model="mealDishes" placeholder="例如：毛肚、虾滑、冰粉" /><div class="expense-fields"><div><label>整桌总花费（可选）</label><div class="money-input"><span>¥</span><input v-model="mealExpense" type="number" min="0" max="1000000" step="0.01" inputmode="decimal" placeholder="例如：268" /></div></div><div><label>我个人的金额（可选）</label><div class="money-input personal"><span>¥</span><input v-model="mealPersonalExpense" type="number" min="0" max="1000000" step="0.01" inputmode="decimal" placeholder="例如：67" /></div></div></div><label>聚餐照片 · 最多 4 张</label><label class="photo-picker">＋ 从相册选择照片<input type="file" accept="image/*" multiple @change="selectMealPhotos" /></label><div v-if="mealPhotoPreviews.length" class="photo-preview-grid"><div v-for="(photo, index) in mealPhotoPreviews" :key="photo"><img :src="photo" alt="待上传照片" /><button aria-label="删除照片" @click="removeMealPhoto(index)">×</button></div></div><label>顺便给餐馆评分（可选）</label><div class="score-picker compact-score"><button v-for="score in [1, 2, 3, 4, 5]" :key="score" :class="{ active: score <= mealRatingScore }" @click="mealRatingScore = mealRatingScore === score ? 0 : score">★</button><strong>{{ mealRatingScore ? `${mealRatingScore}.0` : '不评分' }}</strong></div><label>备注</label><textarea v-model="mealNote" maxlength="300" placeholder="这一顿有什么值得记住？"></textarea><button class="primary wide" :disabled="mutating || uploadingPhotos" @click="submitMeal">{{ uploadingPhotos ? '正在上传照片…' : mutating ? '保存中…' : '保存聚餐' }}</button></section></div>

    <div v-if="showBillForm" class="modal-layer" @click.self="showBillForm = false"><form class="sheet form-sheet bill-form" @submit.prevent="submitBillExpense"><button type="button" class="close" @click="showBillForm = false">×</button><p class="eyebrow">饭桌共享账单</p><h2>补录聚餐花费</h2><label>选择哪一顿</label><select :value="billMealId" class="select-control" @change="handleBillMealChange"><option v-for="meal in allMeals" :key="meal.id" :value="meal.id">{{ formatDate(meal.occurredAt) }} · {{ findRestaurant(meal.restaurantId)?.name ?? '聚餐' }}</option></select><label>整桌总花费（可选）</label><div class="money-input"><span>¥</span><input v-model="billAmount" type="number" min="0" max="1000000" step="0.01" inputmode="decimal" placeholder="例如：整桌 268 元" /></div><label>我个人的金额（可选）</label><div class="money-input personal"><span>¥</span><input v-model="billPersonalAmount" type="number" min="0" max="1000000" step="0.01" inputmode="decimal" placeholder="例如：我承担 67 元" /></div><p class="field-note">总金额是全桌合计；个人金额只写入当前账号。两项可以只填一项。</p><p v-if="billFeedback" class="bill-feedback" role="status" aria-live="polite">{{ billFeedback }}</p><button type="submit" class="primary wide" :disabled="mutating">{{ mutating ? '保存中…' : '保存账单' }}</button></form></div>

    <div v-if="showRestaurantForm" class="modal-layer" @click.self="closeCustomRestaurant"><section class="sheet form-sheet"><button class="close" @click="closeCustomRestaurant">×</button><p class="eyebrow">地图上没有也能记录</p><h2>自建餐馆</h2><label>餐馆名称</label><input v-model="customRestaurantName" maxlength="80" placeholder="例如：学校东门小炒" /><label>分类</label><select v-model="customRestaurantCategory" class="select-control"><option>餐馆</option><option>中餐</option><option>火锅</option><option>烧烤</option><option>快餐</option><option>咖啡甜品</option><option>其他</option></select><label>地址或位置描述（可选）</label><input v-model="customRestaurantAddress" maxlength="200" placeholder="例如：学校东门向南 100 米" /><label class="check-row"><input v-model="customAddToFavorites" type="checkbox" /><span>同时加入全员收藏池</span></label><p class="field-note">自建条目会标记为“成员自建”，不会伪装成地图数据。</p><button class="primary wide" :disabled="mutating" @click="submitCustomRestaurant">{{ mutating ? '保存中…' : '保存餐馆' }}</button></section></div>

    <div v-if="showInstallHelp" class="modal-layer" @click.self="showInstallHelp = false"><section class="sheet help-sheet"><button class="close" @click="showInstallHelp = false">×</button><p class="eyebrow">像 App 一样使用</p><h2>安装到手机桌面</h2><div class="instruction"><span>Android</span><p>使用 Chrome 打开本站，点右上角菜单，再选择“安装应用”或“添加到主屏幕”。也可以下载我们生成的 APK。</p></div><div class="instruction"><span>iPhone</span><p>必须使用 Safari 打开，点击底部“分享”按钮，然后选择“添加到主屏幕”。</p></div><div class="privacy-box"><strong>隐私说明</strong><p>定位只用于本次距离排序，不保存精确位置；昵称、头像、收藏、评分与聚餐记录只供同一邀请码空间的成员查看。</p></div><button class="primary wide" @click="showInstallHelp = false">知道了</button></section></div>

    <transition name="toast"><div v-if="toast" class="toast">{{ toast }}</div></transition>
  </div>
</template>
